import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../lib/api';

// Types for billing data
export interface Estimate {
  id: number;
  document_number: string;
  status: string;
  acceptance_status?: string;
  total_amount: string;
  subtotal: string;
  tax_amount: string;
  discount_amount: string;
  document_date: string;
  valid_until?: string;
  customer_name?: string;
  customer_email?: string;
  created_at: string;
  updated_at: string;
  notes?: string;
  terms?: string;
  converted_to_invoice_id?: number;
}

export interface Invoice {
  id: number;
  document_number: string;
  status: string;
  payment_status: string;
  total_amount: string;
  subtotal: string;
  tax_amount: string;
  discount_amount: string;
  document_date: string;
  due_date?: string;
  customer_name?: string;
  customer_email?: string;
  created_at: string;
  updated_at: string;
  notes?: string;
  terms?: string;
  amount_paid: string;
  balance_due: string;
}

export interface Payment {
  id: number;
  amount: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'refunded' | 'cancelled';
  refunded_amount: string;
  net_amount: string | number;
  payment_date: string;
  payment_method: string;
  reference_number?: string;
  notes?: string;
  created_at: string;
  document_type: string;
  document_id: number;
  document_number?: string;
  invoice?: Invoice;
}

// Hook for fetching job estimates
export const useJobEstimates = (jobId: string | undefined) => {
  return useQuery({
    queryKey: ['jobEstimates', jobId],
    queryFn: async (): Promise<Estimate[]> => {
      if (!jobId) throw new Error('Job ID is required');
      const response = await apiClient.getAllPages<any>(`/api/estimates/?job=${jobId}`);
      return response.data;
    },
    enabled: !!jobId,
  });
};

// Hook for fetching job invoices
export const useJobInvoices = (jobId: string | undefined) => {
  return useQuery({
    queryKey: ['jobInvoices', jobId],
    queryFn: async (): Promise<Invoice[]> => {
      if (!jobId) throw new Error('Job ID is required');
      const response = await apiClient.getAllPages<any>(`/api/invoices/?job=${jobId}`);
      return response.data;
    },
    enabled: !!jobId,
  });
};

// Hook for fetching job payments
export const useJobPayments = (jobId: string | undefined) => {
  return useQuery({
    queryKey: ['jobPayments', jobId],
    queryFn: async (): Promise<Payment[]> => {
      if (!jobId) throw new Error('Job ID is required');
      const response = await apiClient.getAllPages<any>(`/api/payments/?job_id=${jobId}`);
      return response.data;
    },
    enabled: !!jobId,
  });
};

// Hook for fetching all billing data for a job
export const useJobBilling = (jobId: string | undefined) => {
  const estimatesQuery = useJobEstimates(jobId);
  const invoicesQuery = useJobInvoices(jobId);
  const paymentsQuery = useJobPayments(jobId);

  return {
    estimates: estimatesQuery.data || [],
    invoices: invoicesQuery.data || [],
    payments: paymentsQuery.data || [],
    isLoading: estimatesQuery.isLoading || invoicesQuery.isLoading || paymentsQuery.isLoading,
    error: estimatesQuery.error || invoicesQuery.error || paymentsQuery.error,
    refetch: () => {
      estimatesQuery.refetch();
      invoicesQuery.refetch();
      paymentsQuery.refetch();
    }
  };
};
